import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const UserLocationDetails = () => {
  const location = useLocation();
  const { mailDetails, buttonDetails } = location.state || {};
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Custom date formatting function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);

    // Check if it's a valid date
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    // Format the date to "MM/DD/YYYY, hh:mm:ss AM/PM"
    return date.toLocaleString('en-GB', {
     
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    
  };

  // Sort locations by date in descending order
  const sortedLocations = useMemo(() => {
    if (!buttonDetails?.userLocation) return [];
    
    return [...buttonDetails.userLocation].sort((a, b) => {
      const dateA = new Date(a.clickedOn);
      const dateB = new Date(b.clickedOn);
      
      if (dateA && dateB) {
        return dateB.getTime() - dateA.getTime();
      } else {
        // Handle cases where dateA or dateB is null
        return 0;
      }
    });
  }, [buttonDetails]);

  // Pagination 
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedLocations.slice(indexOfFirstItem, indexOfLastItem);

  // Calculate total pages
  const totalPages = Math.ceil(sortedLocations.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (!mailDetails || !buttonDetails) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-gray-600">No data found.</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Click Details</h1>

        {/* Summary Card */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* <div>
              <h3 className="text-sm font-semibold text-gray-600">Button ID</h3>
              <p className="text-sm text-gray-800 break-all">{buttonDetails.buttonId}</p>
            </div> */}
            <div>
              <h3 className="text-sm font-semibold text-gray-600">Button URL</h3>
              <p className="text-sm text-gray-800 break-all">
                <a
                  href={buttonDetails.buttonURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {buttonDetails.buttonURL}
                </a>
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-600">Total Clicks</h3>
              <p className="text-lg text-gray-800">{buttonDetails.clickCount}</p>
            </div>
          </div>
        </div>

        {/* Location Details Table */}
        <div className="overflow-x-auto">
          <h3 className="text-xl font-semibold mb-4 text-gray-800">Location Details</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-blue-500 to-purple-600">
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">No.</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">IP Address</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Timezone</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Country</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Region</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Clicked On</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length > 0 ? (
                currentItems.map((location, index) => (
                  <tr key={index} className="hover:bg-gray-50 border-b">
                    <td className="px-4 py-3 text-gray-800">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="px-4 py-3 text-gray-800">{location.ip || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-800">{location.timezone || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-800">{location.country || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-800">{location.region || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-800">{formatDate(location.clickedOn)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-gray-600">
                    No location data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-100 px-4 py-3 flex items-center justify-center space-x-2 mt-4">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => paginate(index + 1)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border 
                    ${currentPage === index + 1 
                      ? 'bg-blue-500 text-white border-blue-500' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  {index + 1}
                </button>
              ))}
              
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserLocationDetails;
